import { di } from 'react-magnetic-di';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import type { ExportItemType } from '../common/types.tsx';
import type { useFetchAllExportIssuesResources } from '../services/export-issues/index.tsx';
import { ECOSYSTEM_APPS_GROUP_INDEX } from './constants.tsx';

const getAllExportItems = (
	data: ReturnType<typeof useFetchAllExportIssuesResources>['exportItems'],
): ExportItemType[][] | undefined => data && data.filter && data.filter.map((group) => group.items);

export const getExportItems = (
	data: ReturnType<typeof useFetchAllExportIssuesResources>['exportItems'],
): ExportItemType[][] | undefined => getAllExportItems(data);

/** Alphabetically orders connect apps by their labels. Note this function will modify the original array as well.  */
export const sortConnectAppsInExportItems = (
	exportItems: ExportItemType[][] | undefined,
	intlCollator: Intl.Collator,
): ExportItemType[][] | undefined => {
	const orderedExportItems = exportItems;
	orderedExportItems != null &&
		orderedExportItems.length > ECOSYSTEM_APPS_GROUP_INDEX &&
		orderedExportItems[ECOSYSTEM_APPS_GROUP_INDEX]?.sort((a: ExportItemType, b: ExportItemType) =>
			intlCollator.compare(a.label, b.label),
		);

	return orderedExportItems;
};

const receiveSameDomainOverride = (params: { s: string }) => {
	const { s: command } = params;

	if (command !== 'hideLoading' && command !== 'showLoading') {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.gadgets.rpc.originalReceiveSameDomain(params);
	}
};

export const overrideIframeRpcCall = (): void => {
	// NOTE - In the past iframe gadgets in dashboards use rpc calls to talk to SPA but
	// we don't do it in NIN. A noop needs to be registered to showLoading and hideLoading
	// to overwrite the original that would throw an error. This is a temporary measure to
	// prevent Sentry noise - iframe gadgets will be deprecated in the near future.
	if (
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window?.gadgets?.rpc?.receiveSameDomain &&
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.gadgets.rpc.receiveSameDomain !== receiveSameDomainOverride
	) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.gadgets.rpc.originalReceiveSameDomain = window.gadgets.rpc.receiveSameDomain;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.gadgets.rpc.receiveSameDomain = receiveSameDomainOverride;
	}
};

type QueryParams = {
	excludeDoneWorkItems: boolean;
};

export function appendQueryParamInternal(
	path: string,
	queryParams: QueryParams = { excludeDoneWorkItems: false },
): string {
	di(window);

	// If there's an issue parsing the URL return origin path
	let url: URL;
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		url = new URL(path, window.location.origin);
	} catch (e) {
		return path;
	}

	const params = new URLSearchParams(url.search);
	for (const [key, value] of Object.entries(queryParams)) {
		if (value) {
			params.append(key, value.toString());
		}
	}
	let paramsAsString = '';
	if (params.size) {
		paramsAsString = `?${params}`;
	}

	return `${url.pathname}${paramsAsString}`;
}

export const appendQueryParam = functionWithCondition<
	(path: string, queryParams?: QueryParams) => string
>(
	() => fg('jsc_exports_hide_done_work_items'),
	appendQueryParamInternal,
	(path: string) => path,
);
